<template>
<div class="uplot-stacked" :class="{'dark-u-plot': isDark, 'screenshot-u-plot': screenshotPending}">
    <div ref="plot-numeric"></div>
    <div ref="plot-state"></div>
    <div ref="legend" class="plotLegend">
        <div
            v-for="(s, idx) in optionsNumeric.series"
            :key="idx"
            v-bind:class="{ disable: !s.show, xseries: idx === 0  }"
            @click="toggleSeries(idx)"
        >
            <span class="legend-key" :style="{color: s.stroke}">
                {{(s.dash && s.dash.length) ? '- -' : '&mdash;'}}
            </span>
            {{s.label}}
        </div>
    </div>
    <div
        ref="tooltip"
        class="tooltip"
        v-show="showTooltip && allowTooltip"
        :style="{left: `${xToolTip}px`, top: `${yToolTip}px`, display: embedded ? 'absolute' : 'fixed'}"
    >
        <table>
            <tbody>
                <tr>
                    <td></td>
                    <td colspan="3">{{xValTooltip}}</td>
                </tr>
                <tr v-for="(s, i) in dataTooltip" :key="i">
                    <th
                        class="legend-key"
                        :style="{color: typeof s.series.stroke === 'string' ? s.series.stroke : s.series.stroke[s.data]}"
                    >
                        {{s.series.dash && s.series.dash.length ? '- -' : '&mdash;'}}
                    </th>
                    <td>{{s.series.label}}</td>
                    <td :colspan="s.series.units ? '1': '2'">
                        <span v-if="s.series.i18n">{{s.series.i18n(s.data)}}</span>
                        <span v-else>{{s.data}}</span>
                    </td>
                    <td v-if="s.series.units">
                        {{s.series.units}}
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</div>
</template>

<script>
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import 'path2d-polyfill';
import { stringToColor, contrastColorString, debounce } from '@/services/Utils.js';
import html2canvas from 'html2canvas';

const plotSetScaleSafe = (plot, axis, nscale) => {
    const {min, max} = nscale;
    if (isNaN(min) || isNaN(max)) {
        console.error(`Invalid Min/Max for scale min=${min}, max=${max}`);
        return;
    }
    plot.setScale(axis, nscale);
}

function clamp(nRange, nMin, nMax, fRange, fMin, fMax) {
    if (nRange > fRange) {
        nMin = fMin;
        nMax = fMax;
    }
    else if (nMin < fMin) {
        nMin = fMin;
        nMax = fMin + nRange;
    }
    else if (nMax > fMax) {
        nMax = fMax;
        nMin = fMax - nRange;
    }
    return [nMin, nMax];
}

const panPlugin = (opts) => {
    let onStart = opts.onStart || (() => {});
    let onEnd = opts.onEnd || (() => {});
    let onChange = opts.onChange || (() => {});
    return {
        hooks: {
            ready: u => {
                let plot = u.root.querySelector(".u-over");
                let xDown, xScaleMin, xScaleMax, xUnitsPerPx;
                let yDown, yScaleMin, yScaleMax, yUnitsPerPx;

                function onup(e) {
                    document.removeEventListener("mousemove", onmove);
                    document.removeEventListener("mouseup", onup);
                    onEnd(u, u.scales.x, u.scales.y);
                }

                function onmove(e) {
                    e.preventDefault();
                    let dx = xUnitsPerPx * (e.clientX - xDown);
                    let dy = yUnitsPerPx * (e.clientY - yDown);
                    let xScale = { min: xScaleMin - dx, max: xScaleMax - dx };
                    let yScale = { min: yScaleMin - dy, max: yScaleMax - dy };
                    u.batch(() => {
                        if (opts.x === undefined || opts.x)
                            plotSetScaleSafe(u, "x", xScale);
                        if (opts.y === undefined || opts.y)
                            plotSetScaleSafe(u, "y", yScale);
                    });
                    onChange(u, xScale, yScale);
                }

                plot.addEventListener("mousedown", e => {
                    if (e.button == 1) {
                        e.preventDefault();

                        xDown = e.clientX;
                        yDown = e.clientY;
                        xScaleMin = u.scales.x.min;
                        xScaleMax = u.scales.x.max;
                        yScaleMin = u.scales.y.min;
                        yScaleMax = u.scales.y.max;
                        xUnitsPerPx = u.posToVal(1, 'x') - u.posToVal(0, 'x');
                        yUnitsPerPx = u.posToVal(1, 'y') - u.posToVal(0, 'y');
                        onStart(u, u.scales.x, u.scales.y);

                        document.addEventListener("mousemove", onmove);
                        document.addEventListener("mouseup", onup);
                    }
                });
            }
        }
    }
}

const zoomWheelPlugin = (opts) => {
    opts = opts || {};
    const factor = opts.factor || 0.75;
    const onEnd = opts.onEnd || (() => {});
    opts.x = opts.x || true;
    opts.y = opts.y || true;

    return {
        hooks: {
            ready: u => {
                const xMin = u.scales.x.min, xMax = u.scales.x.max;
                const yMin = u.scales.y.min, yMax = u.scales.y.max;
                const xRange = xMax - xMin, yRange = yMax - yMin;

                let plot = u.root.querySelector(".u-over");

                plot.addEventListener("wheel", e => {
                    e.preventDefault();

                    let rect = plot.getBoundingClientRect();
                    let {left, top} = u.cursor;

                    let leftPct = left/rect.width;
                    let btmPct = 1 - top/rect.height;
                    let xVal = u.posToVal(left, "x");
                    let yVal = u.posToVal(top, "y");
                    let oxRange = u.scales.x.max - u.scales.x.min;
                    let oyRange = u.scales.y.max - u.scales.y.min;

                    let nxRange = e.deltaY < 0 ? oxRange * factor : oxRange / factor;
                    let nxMin = xVal - leftPct * nxRange;
                    let nxMax = nxMin + nxRange;
                    if (opts.clampX)
                        [nxMin, nxMax] = clamp(nxRange, nxMin, nxMax, xRange, xMin, xMax);

                    let nyRange = e.deltaY < 0 ? oyRange * factor : oyRange / factor;
                    let nyMin = yVal - btmPct * nyRange;
                    let nyMax = nyMin + nyRange;
                    if (opts.clampY)
                        [nyMin, nyMax] = clamp(nyRange, nyMin, nyMax, yRange, yMin, yMax);

                    let nxScale = { min: Math.round(nxMin), max: Math.round(nxMax) }
                    let nyScale = { min: Math.round(nyMin), max: Math.round(nyMax) };
                    u.batch(() => {
                        if (opts.x)
                            plotSetScaleSafe(u, "x", nxScale);
                        if (opts.y)
                            plotSetScaleSafe(u, "y", nyScale);
                    });
                    onEnd(u, nxScale, nyScale);
                });
            }
        }
    }
}

const touchPlugin = (opts) => {
    let onStart = opts.onStart || (() => {});
    let onEnd = opts.onEnd || (() => {});
    let onChange = opts.onChange || (() => {});
    const touches = opts.touches || 1;
    let zoomed = false;
    function init(u) {
        const xMin = u.scales.x.min, xMax = u.scales.x.max;
        const yMin = u.scales.y.min, yMax = u.scales.y.max;
        const xRange = xMax - xMin, yRange = yMax - yMin;
        let plot = u.root.querySelector(".u-over");
        let rect, oxRange, oyRange, xVal, yVal;
        let fr = {x: 0, y: 0, dx: 0, dy: 0};
        let to = {x: 0, y: 0, dx: 0, dy: 0};

        function storePos(t, e) {
            let ts = e.touches;

            let t0 = ts[0];
            let t0x = t0.clientX - rect.left;
            let t0y = t0.clientY - rect.top;

            if (ts.length == 1) {
                t.x = t0x;
                t.y = t0y;
                t.d = 0;
            }
            else {
                let t1 = e.touches[1];
                let t1x = t1.clientX - rect.left;
                let t1y = t1.clientY - rect.top;

                let xMin = Math.min(t0x, t1x);
                let yMin = Math.min(t0y, t1y);
                let xMax = Math.max(t0x, t1x);
                let yMax = Math.max(t0y, t1y);

                // midpts
                t.y = (yMin+yMax)/2;
                t.x = (xMin+xMax)/2;

                t.dx = xMax - xMin;
                t.dy = yMax - yMin;

                // dist
                t.d = Math.sqrt(t.dx * t.dx + t.dy * t.dy);
            }
        }

        let rafPending = false;

        function zoom() {
            rafPending = false;

            let left = to.x;
            let top = to.y;

            // non-uniform scaling
            //	let xFactor = fr.dx / to.dx;
            //	let yFactor = fr.dy / to.dy;

            // uniform x/y scaling
            let xFactor = 1;
            let yFactor = 1;
            if (fr.d !== 0 && to.d !== 0) {
                xFactor = fr.d / to.d;
                yFactor = fr.d / to.d;
            }

            let leftPct = left/rect.width;
            let btmPct = 1 - top/rect.height;

            let nxRange = oxRange * xFactor;
            let nxMin = xVal - leftPct * nxRange;
            let nxMax = nxMin + nxRange;
            if (opts.clampX)
                [nxMin, nxMax] = clamp(nxRange, nxMin, nxMax, xRange, xMin, xMax);

            let nyRange = oyRange * yFactor;
            let nyMin = yVal - btmPct * nyRange;
            let nyMax = nyMin + nyRange;
            if (opts.clampY)
                [nyMin, nyMax] = clamp(nyRange, nyMin, nyMax, yRange, yMin, yMax);

            let nxScale = { min: nxMin, max: nxMax, }, nyScale = { min: nyMin, max: nyMax, };
            u.batch(() => {
                plotSetScaleSafe(u, "x", nxScale);
                plotSetScaleSafe(u, "y", nyScale);
            });
            onChange(u, nxScale, nyScale);
        }

        function touchmove(e) {
            if (e.touches.length >= touches) {
                e.preventDefault()
                storePos(to, e);

                if (!zoomed) {
                    zoomed = true;
                    onStart(u, u.scales.x, u.scales.y);
                }

                if (!rafPending) {
                    rafPending = true;
                    requestAnimationFrame(zoom);
                }
                return false
            }
        }

        plot.addEventListener("touchstart", function(e) {
            rect = plot.getBoundingClientRect();

            storePos(fr, e);

            oxRange = u.scales.x.max - u.scales.x.min;
            oyRange = u.scales.y.max - u.scales.y.min;

            let left = fr.x;
            let top = fr.y;

            xVal = u.posToVal(left, "x");
            yVal = u.posToVal(top, "y");

            document.addEventListener("touchmove", touchmove, {passive: false});
        });

        plot.addEventListener("touchend", function(e) {
            if (zoomed) {
                zoomed = false;
                onEnd(u, u.scales.x, u.scales.y);
            }
            document.removeEventListener("touchmove", touchmove, {passive: false});
        });
    }

    return {
        hooks: {
            ready: init
        }
    };
}

const cursorNotify = (options) => {

    function init(u, opts, data) {
        const plot = u.root.querySelector(".u-over");
        plot.addEventListener("mouseleave", () => {
            if (!u.cursor.locked)
                options.hide();
        });
        plot.addEventListener("mouseenter", options.show );
        options.hide();
    }

    function setCursor(u) {
        const {left, top, idx} = u.cursor;

        if (idx === 0 || idx) {
            options.update(u, idx, left, top);
        }
        else {
            options.hide();
        }
    }

    return { hooks: { init, setCursor } };
}

function getPos(pos, elSize, contSize, base, offset) {
    let rtn;
    let wOffset = pos + offset;
    if (wOffset + elSize > contSize) { // to wide to fit tot he right of cursor
        let loc = pos - elSize - offset;
        rtn = (base + (loc > 0 ? loc : 0));
    }
    else // will fit to the right of cursor
        rtn = (base + wOffset);
    return rtn;
}

const statePlugin = (idx, opts) => {
    opts = opts || {};
    const yval = opts.yval || 0;
    const lineWidth = (opts.width || 1);
    const strokes = opts.stroke || {};
    const textOffset = opts.textOffset || 5;
    const type = opts.type || 'bar';
    const i18n = opts.i18n || (v => v);
    
    const labelColors = {};

    function getStroke(state) {
        if (state in strokes) return strokes[state];
        return strokes[state] = stringToColor(state, type == 'background' ? 0.1: 1);
    }

    function getLabelColor(color) {
        if (color in labelColors) return labelColors[color];
        return labelColors[color] = contrastColorString(color);
    }

    function init (u) { u.series[idx].value = (u, v) => v; }

    function drawSeries (u, s) {
        if (s != idx) return;
        let { ctx } = u;
        let { left, top, width, height } = u.bbox;
        let { scale } = u.series[s];
        let rLabel, rTop, rHeight;
        let ypos = u.valToPos(yval - lineWidth / 2, scale, true);
        let rectHeight = u.valToPos(yval + lineWidth / 2, scale, true) - ypos;
        rectHeight = type == 'bar' ? rectHeight : height;

        ctx.save();
        ctx.beginPath();
        ctx.rect(left, top, width, height);
        ctx.clip();
        ctx.textAlign = "left";

        if (type == 'bar') {
            rTop = u.valToPos(yval - lineWidth / 2, scale, true);
            rHeight = u.valToPos(yval + lineWidth / 2, scale, true) - rTop;
            rLabel = u.valToPos(yval, scale, true);
            ctx.font = ctx.font = `${Math.floor(rHeight * 0.9)}px Arial`;
            ctx.textBaseline = "center";
        }
        else {
            rTop = top;
            rHeight = height;
            rLabel = top + textOffset;
            ctx.textBaseline = "top";
        }

        let i = 0, len = u.data[0].length;
        while (i < len-1) {
            let start = u.valToPos(u.data[0][i], 'x', true);
            let state = u.data[idx][i];
            while (state === u.data[idx][i] && i < len-1) i++;
            let rWidth = u.valToPos(u.data[0][i], 'x', true) - start;
            ctx.strokeStyle = ctx.fillStyle = getStroke(state);
            ctx.beginPath();
            ctx.rect(start, rTop, rWidth, rHeight);
            ctx.closePath();
            ctx.fill();
            ctx.fillStyle = getLabelColor(ctx.fillStyle);
            if ((rWidth) > (ctx.measureText(state).width + textOffset))
                ctx.fillText(i18n(state), start + textOffset, rLabel);
        }
        ctx.restore();
    }

    return {
        opts: (u, opts) => { opts.series[idx].paths = () => null; /* Cannot draw string data */},
        hooks: { init, drawSeries }
    }
}

const secondsToTimeObj = value => {
    let hours = Math.floor(value / 3600);
    let tsecs = value % 3600;
    let minutes = Math.floor(tsecs / 60);
    return {hours: hours, minutes: minutes, seconds: Math.round(tsecs % 60)};
}

const secToHms = (secs, showSeconds) => {
    const time = secondsToTimeObj(secs);
    let rtn = String(time.hours);
    rtn = `${rtn}:${time.minutes >= 10 ? time.minutes : '0' + String(time.minutes)}`;
    if (showSeconds === undefined ? true : showSeconds)
        rtn = `${rtn}:${time.seconds >= 10 ? time.seconds : '0' + String(time.seconds)}`;
    return rtn;
}

export default {
    props: {
        data: { type: Array },
        series: { type: Array },
        yRange: { type: Array },
        xRange: { type: Array },
        dark: { type: Boolean, default: false },
        yAxisLabel: { type: String, default: '' },
        tz: { type: String },
        interactive: { type: Boolean, default: false },
        duration: { type: Boolean, default: false },
        yAxisLabelSize: { type: Number, default: 80 },
        embedded: { type: Boolean, default: false },
    },
    data () { return {
        plotNumeric: null,
        plotState: null,
        dataNumeric: [],
        dataState: [],
        optionsNumeric: [],
        optionsState: [],
        height: 200,
        width: 200,
        heightPlotState: 0,
        allowTooltip: true,
        showTooltip: false,
        xToolTip: 0,
        yToolTip: 0,
        dataTooltip: [],
        xValTooltip: '',
        emitScaleChangeEnd: debounce(() => {
            const scales = (this.showPlotNumeric ? this.plotNumeric : this.plotState).scales;
            this.$emit('scaleChangeEnd', scales);
        }, 250),
        screenshotPending: false,
        darkTick: '#272727',
        lightTick: 'rgba(0,0,0,0.07)',
    }},
    computed: {
        seriesNumeric () { return this.filterSeries(['num']); },
        seriesState () { return this.filterSeries(['state', 'bool']); },
        showPlotNumeric () { return !!this.seriesNumeric.length; },
        showPlotState () { return !!this.seriesState.length; },
        isDark () { return this.screenshotPending ? false : this.dark },
    },
    methods: {
        heightLegend () {
            const lclh = this.$refs.legend.clientHeight
            return lclh < 32 ? 32 : lclh;
        },
        heightPlotNumeric () {
            return this.height - (this.heightPlotState + this.heightLegend());
        },
        makePlugins (isNumeric) {
            const pluginOpts = {
                onStart: this.onScaleChangeStart,
                onEnd: this.onScaleChangeEnd,
                onChange: this.onScaleChange,
                clampX: !!this.xRange,
                clampY: !!this.yRange,
                y: isNumeric,
            }
            const csr = cursorNotify({
                show: this.enableTooltip,
                hide: this.disableTooltop,
                update: this.updateTooltip
            });
            if (!this.interactive)
                return [csr];
            return [
                panPlugin(pluginOpts),
                zoomWheelPlugin(pluginOpts),
                touchPlugin(pluginOpts),
                csr
            ];
        },
        makeOptionsCommon () {
            const rtn = {
                width: this.width, height: this.height,
                legend: {show: false},
                series: [{}],
                cursor: {
                    y: false,
                    sync: { key: "uPlot", scales: ['x'] },
                    drag: {x: false, y:false }
                },
                scales: {},
                hooks: {setSelect:[this.onSelectionEnd]},
                plugins: [],
                axes: [ {}, { size: this.yAxisLabelSize, labelSize: 20, label: ' '}]
            }
            if (this.interactive)
                rtn.cursor = {
                    drag: { x: true, y: false, uni: 50, dist: 20 },
                    sync: { key: "uPlot", scales: ['x'] },
                    y: false
                }
            if (this.duration) {
                rtn.scales.x = { time: false };
                rtn.axes[0].values = (u, vals) => vals.map(v => secToHms(v, (vals[vals.length - 1] - vals[0]) <= 600));
            }
            if (this.isDark) {
                for (let i = 0; i < rtn.axes.length; i++) {
                    rtn.axes[i].stroke = '#FFF';
                    rtn.axes[i].grid = {stroke: this.darkTick};
                    rtn.axes[i].ticks = {stroke: this.darkTick};
                }
                this.$el.classList.add('dark-u-plot');
            }
            if (this.tz) rtn.tzDate = ts => uPlot.tzDate(new Date(ts * 1e3), this.tz);
            return rtn
        },
        makeOptionsNumeric () {
            const rtn = this.makeOptionsCommon();
            rtn.plugins.push(...this.makePlugins(true));
            if (this.yRange !== undefined)
                rtn.scales.y = {auto: false, range: this.yRange};
            // This breaks zooming, is this an issue with uPlot? retry after updating...
            // behavior is different on y axis...
            // if (this.xRange !== undefined && rtn.scales.x) {
            //     rtn.scales.x.auto = false;
            //     rtn.scales.x.range = this.xRange;
            // }
            // else if (this.xRange !== undefined)
            //     rtn.scales.x = {auto: false, range: this.xRange};
            rtn.axes[1].label = this.yAxisLabel;
            if (this.interactive)
                rtn.cursor.drag.y = true;
            this.seriesNumeric.forEach(s => rtn.series.push(s));
            rtn.height = this.heightPlotNumeric();
            return rtn;
        },
        makeOptionsState () {
            const stateLabels = {};
            const rtn = this.makeOptionsCommon();
            const origin = -100;
            const count = this.seriesState.length;
            let yValue = origin, yValueOffset = 1;
            rtn.plugins.push(...this.makePlugins(false));
            this.seriesState.forEach(s => {
                const idx = rtn.series.push(s) - 1;
                rtn.plugins.push(statePlugin(idx, {
                    type: 'bar',
                    label: s.label,
                    yval: yValue,
                    width: 0.8,
                    stroke: s.stroke,
                    i18n: s.i18n
                }));
                rtn.series[idx].value = (u, v) => s.i18n(v);
                stateLabels[yValue.toString()] = s.label;
                yValue -= yValueOffset;
            });
            if (this.seriesNumeric) {
                this.heightPlotState = (count + 1) * 20;
                rtn.height = this.heightPlotState;
                rtn.axes[0].size = 1;
            }
            rtn.scales.y = {range: [ yValue + 0.5, origin + 0.5]};
            rtn.axes[1].space = (u) => u.root.querySelector(".u-over").clientHeight / (count + 1);
            rtn.axes[1].values = (u, vals) => vals.map(i => stateLabels[i.toString()] || '');
            return rtn;
        },
        filterData (data, types) {
            return data.filter((_, i) => !i || types.includes(this.series[i-1].ptype));
        },
        filterSeries (types) {
            return this.series.filter(s => types.includes(s.ptype));
        },
        filterDataSets (data) {
            this.dataNumeric = this.filterData(data, ['num']);
            this.dataState = this.filterData(data, ['state', 'bool']);
        },
        init () {
            this.destroy();
            this.optionsState = this.makeOptionsState();
            this.optionsNumeric = this.makeOptionsNumeric();
            this.filterDataSets(this.data);
            if (this.showPlotState) {
                this.plotState = uPlot(
                    this.optionsState,
                    this.dataState,
                    this.$refs['plot-state']
                );
            }
            if (this.showPlotNumeric) {
                this.plotNumeric = uPlot(
                    this.optionsNumeric,
                    this.dataNumeric,
                    this.$refs['plot-numeric']
                );
            }
        },
        destroy () {
            if (this.plotNumeric) {
                this.plotNumeric.destroy();
                this.plotNumeric = null;
            }
            if (this.plotState) {
                this.plotState.destroy();
                this.plotState = null;
            }
        },
        resize () {
            // height cannot completly fill this.$el it will cause overflow
            // a constant 8px "padding" seems to work to prevent overflow
            this.height = this.$el.clientHeight - 8;
            this.width = this.$el.clientWidth;
            if (this.plotNumeric)
                this.plotNumeric.setSize({width: this.width, height: this.heightPlotNumeric()});
            if (this.plotState)
                this.plotState.setSize({width: this.width, height: this.heightPlotState});
        },
        onResize () {
            //sometimes two resizes are required...
            this.resize();
            setTimeout(() => this.resize(), 500);
        },
        toggleSeries (idx) {
            const show = this.optionsNumeric.series[idx].show = !this.optionsNumeric.series[idx].show;
            const {min, max} = this.plotNumeric.scales.y;
            this.plotNumeric.setSeries(idx, {show});
            plotSetScaleSafe(this.plotNumeric, 'y', {min, max});
        },
        enableTooltip () {
            this.showTooltip = true;
        },
        disableTooltop () {
            this.showTooltip = false;
        },
        updateTooltip (u, idx, x, y) {
            this.showTooltip = true;
            const ttd = [];
            const xval = (this.showPlotNumeric? this.dataNumeric : this.dataState)[0][idx];
            if (this.duration)
                this.xValTooltip = secToHms(xval)
            else
                this.xValTooltip = new Date(xval * 1000).toLocaleString();
            this.seriesNumeric.forEach((s, i) => {
                ttd.push({series: s, data: this.dataNumeric[i+1][idx]});
            });
            this.seriesState.forEach((s, i) => {
                ttd.push({series: s, data: this.dataState[i+1][idx]});
            });
            this.dataTooltip = ttd;
        },
        onMouseMove (e) {
            const xOffset = 15, yOffset = 5;
            const box = this.$el;
            const prect = box.getBoundingClientRect();
            const ttc = this.$refs.tooltip;

            //These two paths are really dumb, but the positioning is not making any sense...
            if (this.embedded) {
                let newxToolTip = e.offsetX + e.target.offsetLeft + this.$refs['plot-numeric'].offsetLeft + xOffset;
                let newyToolTip = e.offsetY + e.target.offsetTop + this.$refs['plot-numeric'].offsetTop + yOffset;
                if (newxToolTip + ttc.clientWidth >= box.clientWidth)
                    newxToolTip -= ttc.clientWidth + xOffset*2;
                if (newyToolTip + ttc.clientHeight >= box.clientHeight)
                    newyToolTip -= (newyToolTip + ttc.clientHeight - box.clientHeight);
                this.xToolTip = Math.max(0, newxToolTip);
                this.yToolTip = Math.max(0, newyToolTip);
            }
            else {
                this.xToolTip = getPos(e.clientX - prect.left, ttc.clientWidth, box.clientWidth, prect.left, 15);
                this.yToolTip = getPos(e.clientY - prect.top, ttc.clientHeight, box.clientHeight, prect.top, 5);
            }
        },
        onSelectionEnd (u) {
            const x = {
                min: u.posToVal(u.select.left, 'x'),
                max: u.posToVal(u.select.left + u.select.width, 'x')
            };
            const y = {
                min: u.posToVal(u.select.top + u.select.height, 'y'),
                max: u.posToVal(u.select.top, 'y')
            }
            if (u.root.parentElement == this.$refs['plot-numeric']) {
                this.$emit('scaleChangeEnd', {x, y});
            }
            else {
                this.$emit('scaleChangeEnd', {x});
            }
        },
        onScaleChangeStart () {
            this.$emit('scaleChangeStart');
        },
        onScaleChange (u, x) {
            if (u.root.parentElement == this.$refs['plot-numeric'])
                plotSetScaleSafe(this.plotState, 'x', x);
            else
                plotSetScaleSafe(this.plotNumeric, 'x', x);
            this.$emit('scaleChangeUpdate', {x});
        },
        onScaleChangeEnd (u, x, y) {
            if (this.showPlotNumeric && this.showPlotState) {
                if (u.root.parentElement == this.$refs['plot-numeric'])
                    plotSetScaleSafe(this.plotState, 'x', x);
                else
                    plotSetScaleSafe(this.plotNumeric, 'x', x);
            }
            this.emitScaleChangeEnd();
        },
        pan (scale, factor) {
            const { min, max } = (this.plotNumeric ? this.plotNumeric : this.plotState).scales[scale];
            const delta = (max - min) * factor;
            const nscale = {min: min + delta, max: max + delta};
            if (this.plotNumeric)
                plotSetScaleSafe(this.plotNumeric, scale, nscale);
            if (this.plotState && scale === 'x')
                plotSetScaleSafe(this.plotState, 'x', nscale);
            this.emitScaleChangeEnd();
        },
        zoom (factor) {
            const plot = this.showPlotNumeric ? this.plotNumeric : this.plotState;
            const { x, y } = plot.scales;
            const xd = ((x.max - x.min) * factor) / 2, yd = ((y.max - y.min) * factor)/2;
            const nx = {min: x.min + xd, max: x.max - xd}, ny = {min: y.min + yd, max: y.max - yd}
            if (this.showPlotNumeric) {
                this.plotNumeric.batch(() => {
                    plotSetScaleSafe(this.plotNumeric, 'x', nx);
                    plotSetScaleSafe(this.plotNumeric, 'y', ny);
                });
                this.emitScaleChangeEnd();
            }
            if (this.showPlotState) {
                plotSetScaleSafe(this.plotState, 'x', nx);
                this.emitScaleChangeEnd();
            }
        },
        redraw (data, holdX, holdY) {
            return new Promise((resolve, reject) => {
                try {
                    this.filterDataSets(data);
                    const { x, y } = (this.showPlotNumeric ? this.plotNumeric : this.lotState).scales;
                    if (this.showPlotNumeric) {
                        this.plotNumeric.batch(() => {
                            this.plotNumeric.setData(this.dataNumeric);
                            if (holdX) plotSetScaleSafe(this.plotNumeric, 'x', x);
                            if (holdY) plotSetScaleSafe(this.plotNumeric, 'y', y);
                        });
                    }
                    if (this.showPlotState) {
                        this.plotState.batch(() => {
                            this.plotState.setData(this.dataState);
                            if (holdX) plotSetScaleSafe(this.plotState, 'x', x);
                        });
                    }
                    resolve();
                } catch (error) {
                    reject(error);
                }
            })
        },
        rebuild () { this.init(); },
        setScale(axis, range) {
            if (this.showPlotNumeric)
                plotSetScaleSafe(this.plotNumeric, axis, range);
            if (this.showPlotState && axis === 'x')
                plotSetScaleSafe(this.plotState, axis, range);
        },
        updateOptionsStroke () {
            this.plotNumeric.axes.forEach(a => {
                a.stroke = this.isDark ? '#FFF' : '#000';
                a.grid.stroke = this.isDark ? this.darkTick : this.lightTick;
                a.ticks.stroke = this.isDark ? this.darkTick : this.lightTick;
            })
            this.plotState.axes.forEach(a => {
                a.stroke = this.isDark ? '#FFF' : '#000';
                a.grid.stroke = this.isDark ? this.darkTick : this.lightTick;
                a.ticks.stroke = this.isDark ? this.darkTick : this.lightTick;
            })
        },
        screenshot () {
            this.screenshotPending = true;
            this.updateOptionsStroke();
            this.redraw(this.data, true, true)
            .then(() => {
                return html2canvas(document.querySelector('.uplot-stacked'))
            })
            .then(canvas => {
                const link = document.createElement('a');
                link.download = 'chart.png';
                link.href = canvas.toDataURL('image/png');
                link.click();
                this.screenshotPending = false;
                this.updateOptionsStroke();
                this.redraw(this.data, true, true)
            });
        },
        touchMove () {
            this.allowTooltip = false;
        },
        touchEnd () {
            this.allowTooltip = true;
        },
    },
    mounted () {
        window.addEventListener('resize', this.onResize);
        this.$el.addEventListener('mousemove', this.onMouseMove);
        this.$el.addEventListener('touchmove', this.touchMove);
        this.$el.addEventListener('touchend', this.touchEnd);
        this.init();
        this.onResize();
    },
    beforeDestroy () {
        this.destroy();
        window.removeEventListener('resize', this.onResize);
        this.$el.removeEventListener('mousemove', this.onMouseMove);
        this.$el.removeEventListener('touchmove', this.touchMove);
        this.$el.removeEventListener('touchend', this.touchEnd);
    }
}
</script>

<style>
.dark-u-plot .u-select {
    background: rgba(255,255,255,0.07);
}
</style>

<style scoped>
.uplot-stacked {
    width: 100%;
    height: 100%;
}
.uplot-stacked.screenshot-u-plot {
    background-color: white !important;
}
::v-deep .uplot {
    width: 100%;
}

.tooltip {
    position: fixed;
    font-size: 0.8em;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 4px;
    z-index: 100;
}
.dark-u-plot .tooltip {
    color: black;
}
.tooltip tr * {
    text-align: left;
    padding: 0px 4px 0px 4px;
}

.legend-key {
    font-weight: bold;
    font-size: 1rem;
}

.plotLegend {
    display: inline-block;
    overflow: auto;
    font-size: 0.8em;
    color: black;
}
.dark-u-plot > .plotLegend {
    color: white;
}
.plotLegend > div {
    cursor: pointer;
    display: inline-block;
    margin: 4px 8px 4px 8px;
    white-space: nowrap;
}
.plotLegend > div.disable {
    opacity: 0.2;
}
.plotLegend > div.disable > i {
    opacity: 0.0;
}
.plotLegend > div.xseries {
    display: none;
}
</style>
